import Breadcrumb, {Crumb} from "../../../components/common/Breadcrumbs";
import React, {ChangeEvent, FC, FormEvent, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import Form from "../../../components/common/form/Form";
import InputField from "../../../components/common/form/InputField";
import {OptionType} from "../../../helpers/utils/StyleUtils";
import {createOptionListForSelectTag} from "../../../helpers/utils/Utils";
import SelectField from "../../../components/common/form/SelectField";
import {handleRTKQuery} from "../../../helpers/utils/RtkQueryUtils";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import {Routes as AppRoutes} from "../../../routes";
import ButtonTray from "../../../components/common/form/ButtonTray";
import {
    useCreateBulkLinkedAccountsMutation,
    useDeleteLinkedAccountByIdMutation,
    useGetLinkedAccountByIdQuery,
    useUpdateLinkedAccountMutation
} from "./linkedAccountsApi";
import {useGetEnumsQuery} from "../../../app/globalApi";
import IconButton from "../../../components/common/form/IconButton";
import {FaLink, FaSearch, FaTimes} from "react-icons/fa";
import ModalButton from "../../../components/common/form/ModalButton";
import CommonSelectorModal from "../../../components/common/form/CommonSelectorModal";
import {useGetCollectorByIdQuery, useSearchCollectorQuery} from "../collector/collector";
import {DataTableColumn} from "../../../components/common/datatable/DataTableTypes";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import {FaMagnifyingGlass, FaTrash} from "react-icons/fa6";
import CommonChosenInput from "../../order/editor/Sections/CommonChosenInput";
import {hasPermission} from "../../../helpers/utils/AccessControlUtils";
import {skipToken} from "@reduxjs/toolkit/query";

interface props {
    crumbs: Crumb[];
    title: string,
    btnText: string,
    isReadOnly?: boolean
}

const LinkedAccountForm: FC<props> = ({crumbs, title, btnText, isReadOnly}) => {

    const navigate = useNavigate();
    let {linkedId} = useParams();

    const [createCounty, createCountyApiResponse] = useCreateBulkLinkedAccountsMutation();
    const [updateCounty, updateCountyApiResponse] = useUpdateLinkedAccountMutation();
    const [linkedAccountData, setlinkedAccountData] = useState<any>({});
    const {data: enums} = useGetEnumsQuery("");
    const {data: linkedAccount} = useGetLinkedAccountByIdQuery(linkedId ?? skipToken);
    const [deleteApi, {isLoading}] = useDeleteLinkedAccountByIdMutation();
    const [selectedRow, setSelectedRow] = useState<any>({});
    const [linkedAccountNumbers, setLinkedAccountNumbers] = useState([]);
    const [showCollectorModal, setShowCollectorModal] = useState<boolean>(false);
    const [accountNumbers, setAccountNumbers] = useState<any>([]);

    const {data: collectorData} = useGetCollectorByIdQuery(
        selectedRow.id ?? linkedAccount?.collector?.id,
        {skip: !(selectedRow.id || linkedAccount?.collector?.id)} // Prevent unnecessary API calls
    );

    useEffect(() => {
        if (linkedAccount) {

            setlinkedAccountData({
                accountNumber: linkedAccount?.accountNumber,
                collector: linkedAccount?.collector?.name || "",
                category: linkedAccount?.category ? {
                    label: linkedAccount?.category,
                    value: linkedAccount?.category
                } : null
            });
            setLinkedAccountNumbers(linkedAccount?.linkedAccountNumbers?.map((linkedAccountNumber: any) => ({
                label: linkedAccountNumber,
                value: linkedAccountNumber
            })));
        }
    }, [linkedAccount]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const createOrUpdateLinkedAccount = async () => {
            if (btnText === "Add") {
                let commonObj = {
                    accountNumbers: accountNumbers.map((accountNumber: any) => accountNumber.value),
                    linkedAccountNumbers: linkedAccountNumbers.map((linkedAccountNumber: any) => linkedAccountNumber.value),
                    collector: collectorData,
                    category: linkedAccountData?.category?.value
                };

                return await createCounty(commonObj).unwrap();
            } else {

                let commonObj = {
                    accountNumber: linkedAccountData?.accountNumber,
                    linkedAccountNumbers: linkedAccountNumbers.map((linkedAccountNumber: any) => linkedAccountNumber.value),
                    collector: collectorData,
                    category: linkedAccountData?.category?.value
                };

                return await updateCounty({
                    id: linkedId,
                    requestBody: commonObj,
                }).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdateLinkedAccount, (response: any) => {
            if (response?.success || response?.data?.success) {
                setlinkedAccountData({});
                navigate(AppRoutes.LinkedAccount.path);
            }
        });
    }

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const {name, value} = "target" in e ? e.target : e;
        setlinkedAccountData({
            ...linkedAccountData,
            [name]: value,
        })
    }

    const handleClearCollector = () => {
        setlinkedAccountData(() => ({
            ...linkedAccountData,
            collector: '',
        }));
    };

    const handleOnClick = (e: any) => {
        e.preventDefault();

        setlinkedAccountData({
            ...linkedAccountData,
            collector: selectedRow.name
        })
        setShowCollectorModal(false)

    }

    const columns: DataTableColumn[] = [
        {
            label: "Collector Name", accessor: "name", filterOptions: [{
                filterModalOption: {
                    filterModalPlaceHolder: "Enter Name...",
                    filterModalKey: "name",
                    filterModalIcon: <FaMagnifyingGlass size={14} className={''}/>
                },
                overrideFilterLabel: " ",
                overrideFilterAccessor: "name"
            }],
        },
        {
            label: "State",
            accessor: "state.stateAbbreviation",
        },
        {
            label: "County",
            accessor: "countyName",
        }
    ]

    const handleDelete = async () => {
        if (linkedId) {
            await deleteApi(linkedId).unwrap();
        }
        navigate(AppRoutes.LinkedAccount.path)
    };

    return (
        <>
            <div className="px-4 py-2">

                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs}/>
                    <div className={"justify-between sm:block md:flex lg:flex"}>
                        {linkedId && hasPermission(["LINKACC-D"]) && (
                            <>
                                <ModalButton
                                    triggerButton={
                                        <IconButton
                                            icon={<FaTrash/>}
                                            title="Delete Linked Account"
                                            hasBackground={true}
                                            className="mr-2"
                                        />
                                    }
                                    modal={
                                        <ConfirmationModal
                                            show={true}
                                            setShow={() => {
                                            }}
                                            title="Delete Linked Account"
                                            bodyMessage="Are you sure you want to delete this Linked Account?"
                                            onSubmit={() => handleDelete()}
                                            isLoading={isLoading}
                                        />
                                    }
                                />
                                {hasPermission(["AUDIT-V"]) &&
                                    <AuditableInfoButton obj={linkedAccount}/>}
                            </>
                        )}

                    </div>
                </div>

                <div className="flex justify-center mt-3">
                    <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                        <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-5">{title}</h3>
                        <Form onSubmit={handleSubmit} fields={<>
                            <div className="grid grid-cols-12">

                                {!linkedId ? <div className="col-span-12 grid grid-cols-12 gap-3 mt-2">
                                        <div className="col-span-3 sm:mt-2 lg:mt-0 flex">
                                            <label className="min-w-[100px]">*Account Number</label>
                                        </div>

                                        <div className="relative col-span-9 flex items-center gap-3">
                                            <div className="w-full">
                                                <CommonChosenInput
                                                    placeholder={"Enter Account Numbers..."}
                                                    state={accountNumbers}
                                                    setState={setAccountNumbers}
                                                    isReadOnly={isReadOnly}
                                                    isRequired={false}/>
                                            </div>
                                        </div>
                                    </div> :
                                    <InputField
                                        name="accountNumber"
                                        label="Account Number"
                                        onChange={handleOnChange}
                                        value={linkedAccountData.accountNumber}
                                        disabled={isReadOnly}
                                    />
                                }

                                <InputField
                                    name="collector"
                                    label="Collector"
                                    onChange={handleOnChange}
                                    value={linkedAccountData.collector}
                                    disabled={isReadOnly}
                                    placeholder="Enter Collector..."
                                    icon={<IconButton icon={<FaLink onClick={() => {
                                        if (selectedRow?.id) {
                                            window.open(`/collectors/${selectedRow.id}`, '_blank');
                                        }
                                    }}/>
                                    }
                                                      className={`text-sm  ${linkedAccountData?.collector ? 'text-highlight-4' : 'text-surface-4 cursor-default'
                                                      }`}/>}
                                    onClick={() => {
                                        setShowCollectorModal(true);
                                    }}
                                    className="cursor-pointer"
                                    inputIcon={
                                        linkedAccountData.collector ? (
                                            <FaTimes
                                                className="fa cursor-pointer text-highlight-0 absolute right-5 top-6"
                                                title="Clear"
                                                onClick={handleClearCollector}
                                            />
                                        ) : (
                                            <FaSearch
                                                className="fa cursor-pointer text-highlight-0 absolute right-5 top-6"
                                                title="show Modal"
                                                onClick={() => {
                                                    setShowCollectorModal(true);
                                                }}
                                            />
                                        )
                                    }
                                />

                                <SelectField
                                    placeholder='Select Category...'
                                    value={linkedAccountData?.category}
                                    options={createOptionListForSelectTag(enums?.["LinkCategory"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({name: "category", value: selectedOption})
                                    }}
                                    label={"Category"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />


                                <div className="col-span-12 grid grid-cols-12 gap-3 mt-2">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 flex">
                                        <label className="min-w-[100px]">Linked Account Numbers</label>
                                    </div>

                                    <div className="relative col-span-9 flex items-center gap-3">
                                        <div className="w-full">
                                            <CommonChosenInput
                                                placeholder={"Enter Linked Account Numbers..."}
                                                state={linkedAccountNumbers}
                                                setState={setLinkedAccountNumbers}
                                                isRequired={false} isReadOnly={isReadOnly}/>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {!isReadOnly &&

                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    </div>
                                    <div className="relative col-span-9">
                                        <div className={"mt-4 flex"}>
                                            <ButtonTray
                                                buttons={[
                                                    {
                                                        buttonProps: {
                                                            btnText: "Cancel",
                                                            onClick: () => navigate(AppRoutes.LinkedAccount.path),
                                                            type: "close",
                                                        },
                                                        buttonType: "button"
                                                    },
                                                    {
                                                        buttonProps: {
                                                            btnText: "Submit",
                                                            type: "submit",
                                                            isLoading: (createCountyApiResponse.isLoading || updateCountyApiResponse.isLoading),
                                                        },
                                                        buttonType: "button",

                                                    }
                                                ]}
                                                align="center"
                                                gap={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }
                        </>}
                        />

                    </div>
                </div>
                {showCollectorModal &&
                    <CommonSelectorModal
                        title="Select Collector"
                        columns={columns}
                        data={useSearchCollectorQuery}
                        show={showCollectorModal}
                        onSubmit={handleOnClick}
                        customerFilter={{
                            sort: "name",
                            sortOrder: "ASCENDING"
                        }}
                        setShow={setShowCollectorModal}
                        setSelectedRow={setSelectedRow}
                    />
                }
            </div>
        </>
    )
}
export default LinkedAccountForm;